import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Moon,
  Droplet,
  ListPlus,
  Star,
  Loader2,
  Plus,
  Trash2,
  Sparkles,
  RotateCcw,
  Settings,
  X as XIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import FoodPhotoButton from "@/components/FoodPhotoButton";
import RetroMacroBackfill from "@/components/vitals/RetroMacroBackfill";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_TYPES = [
  { key: "shake",       label: "Shake",       icon: "🥤" },
  { key: "preworkout",  label: "Pre-Workout", icon: "⚡" },
  { key: "supplement",  label: "Supplement",  icon: "💊" },
  { key: "restaurant",  label: "Restaurant",  icon: "🍽️" },
  { key: "cheat",       label: "Cheat Meal",  icon: "🍕" },
  { key: "other",       label: "Other",       icon: "🍱" },
];

export default function DailyVitals() {
  const { user, refreshUser } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/vitals/today`);
      setSummary(data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  if (!user) {
    return (
      <section
        id="daily-vitals"
        data-testid="daily-vitals"
        className="py-16 bg-[#0A0A0A]"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Daily Vitals
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-white mt-2 mb-3">
            Sleep · Hydrate · Fuel
          </h2>
          <p className="text-neutral-500 text-sm max-w-xl mx-auto">
            Sign in to log your sleep, water, and quick meals — earn Phoenix
            Tokens for hitting your daily goals.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="daily-vitals"
      data-testid="daily-vitals"
      className="py-16 md:py-24 bg-[#0A0A0A]"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
            Daily Vitals
          </div>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-none">
            Sleep · Hydrate · Fuel
          </h2>
          <p className="text-neutral-500 text-sm mt-3 max-w-2xl mx-auto">
            Three quick logs to round out your Map The Work. Streaks reset at
            00:00 Pacific — same as everything else.
          </p>
        </div>

        {loading ? (
          <div className="text-center text-neutral-500 py-10">
            <Loader2 className="animate-spin inline mr-2" size={16} />
            Loading vitals…
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SleepCard summary={summary} onChange={() => { load(); refreshUser?.(); }} />
            <WaterCard summary={summary} onChange={() => { load(); refreshUser?.(); }} />
            <QuickLogCard summary={summary} onChange={() => { load(); refreshUser?.(); }} />
          </div>
        )}
      </div>
    </section>
  );
}

/* ============ Sleep Card ============ */
function SleepCard({ summary, onChange }) {
  const s = summary?.sleep || {};
  const streak = summary?.sleep_streak || { current: 0, longest: 0 };
  const [editing, setEditing] = useState(false);
  const [hours, setHours] = useState(s.hours || "");
  const [quality, setQuality] = useState(s.quality || 0);
  const [bed, setBed] = useState(s.bed_time || "");
  const [wake, setWake] = useState(s.wake_time || "");
  const [note, setNote] = useState(s.note || "");
  const [busy, setBusy] = useState(false);

  // Reset form when summary changes (e.g., after save or another tab edit)
  useEffect(() => {
    setHours(s.hours || "");
    setQuality(s.quality || 0);
    setBed(s.bed_time || "");
    setWake(s.wake_time || "");
    setNote(s.note || "");
  }, [s.hours, s.quality, s.bed_time, s.wake_time, s.note]);

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { quality: quality || null, note };
      if (bed && wake) {
        payload.bed_time = bed;
        payload.wake_time = wake;
      } else if (hours) {
        payload.hours = parseFloat(hours);
      } else {
        toast.error("Enter hours or both bed + wake time.");
        setBusy(false);
        return;
      }
      const { data } = await axios.post(`${API}/vitals/sleep`, payload);
      if (data.hit_goal) {
        toast.success(`😴 Logged ${data.entry.hours}h sleep — +10 Phoenix Tokens! Streak ${data.sleep_streak.current}🔥`, { duration: 4500 });
      } else {
        toast.info(`😴 Logged ${data.entry.hours}h sleep. Aim for 7+ to earn Phoenix Tokens.`, { duration: 4500 });
      }
      if ((data.new_badges || []).length) {
        toast.success(`🏆 New badge${data.new_badges.length > 1 ? "es" : ""} unlocked!`);
      }
      if (data.vitals_mega?.awarded) {
        toast.success(`🐦‍🔥 TOTAL PHOENIX! +${data.vitals_mega.bonus_pt} Phoenix Tokens — all 6 vitals tiles cleared this week!`, { duration: 8000 });
      }
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const logged = !!s.hours;

  return (
    <div
      data-testid="vitals-sleep-card"
      className="bg-[#0F0F0F] border border-zinc-800 p-5"
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-300 font-bold flex items-center gap-1.5">
            <Moon size={11} /> Sleep
          </div>
          <h3 className="font-display text-2xl text-white">Last Night</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Streak</div>
          <div className="font-display text-2xl text-[#D4AF37] tabular-nums">{streak.current}d</div>
        </div>
      </div>

      {!editing && logged ? (
        <div>
          <div className="flex items-baseline gap-2 mb-2">
            <span className="font-display text-5xl text-white tabular-nums">{s.hours}</span>
            <span className="text-sm text-neutral-500">hrs</span>
            {s.quality ? (
              <span className="ml-auto flex gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    size={12}
                    className={n <= s.quality ? "text-[#D4AF37] fill-[#D4AF37]" : "text-neutral-700"}
                  />
                ))}
              </span>
            ) : null}
          </div>
          {(s.bed_time || s.wake_time) && (
            <div className="text-[11px] text-neutral-500 mb-2">
              {s.bed_time || "—"} → {s.wake_time || "—"}
            </div>
          )}
          {s.note && (
            <p className="text-xs text-neutral-300 italic border-l-2 border-indigo-700 pl-2 mb-3">
              "{s.note}"
            </p>
          )}
          <button
            data-testid="vitals-sleep-edit"
            onClick={() => setEditing(true)}
            className="btn-omni btn-outline-gold w-full text-xs"
          >
            <Settings size={12} /> Edit Tonight's Sleep
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
                Bed Time
              </label>
              <input
                data-testid="vitals-sleep-bed"
                type="time"
                value={bed}
                onChange={(e) => setBed(e.target.value)}
                className="input-omni w-full"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
                Wake Time
              </label>
              <input
                data-testid="vitals-sleep-wake"
                type="time"
                value={wake}
                onChange={(e) => setWake(e.target.value)}
                className="input-omni w-full"
              />
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-600 text-center">
            — or —
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
              Hours (decimal ok)
            </label>
            <input
              data-testid="vitals-sleep-hours"
              type="number"
              step="0.25"
              min="0"
              max="14"
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              placeholder="7.5"
              className="input-omni w-full"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
              Quality
            </label>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  data-testid={`vitals-sleep-quality-${n}`}
                  onClick={() => setQuality(quality === n ? 0 : n)}
                  className="flex-1 py-2 border border-zinc-800 hover:border-[#D4AF37]"
                  title={`${n}/5`}
                >
                  <Star
                    size={14}
                    className={`mx-auto ${
                      n <= quality ? "text-[#D4AF37] fill-[#D4AF37]" : "text-neutral-700"
                    }`}
                  />
                </button>
              ))}
            </div>
          </div>
          <textarea
            data-testid="vitals-sleep-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="How do you feel today? (optional, 300 chars)"
            maxLength={300}
            className="input-omni w-full min-h-[60px]"
          />
          <div className="flex gap-2">
            {logged && (
              <button
                data-testid="vitals-sleep-cancel"
                onClick={() => setEditing(false)}
                className="btn-omni btn-outline-gold text-xs flex-1"
              >
                Cancel
              </button>
            )}
            <button
              data-testid="vitals-sleep-save"
              onClick={submit}
              disabled={busy}
              className="btn-omni btn-red text-xs flex-1 disabled:opacity-50"
            >
              {busy ? <Loader2 className="animate-spin" size={12} /> : <Sparkles size={12} />}
              {logged ? "Update" : "Log Sleep"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ Water Card ============ */
function WaterCard({ summary, onChange }) {
  const w = summary?.water || { total_oz: 0, goal_oz: 64, entries: [] };
  const streak = summary?.water_streak || { current: 0, longest: 0 };
  const [showCustom, setShowCustom] = useState(false);
  const [customOz, setCustomOz] = useState("");
  const [showGoal, setShowGoal] = useState(false);
  const [newGoal, setNewGoal] = useState(w.goal_oz);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNewGoal(w.goal_oz);
  }, [w.goal_oz]);

  const addOz = async (oz, label = "") => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/vitals/water/add`, { oz, label });
      if (data.crossed_goal) {
        toast.success(`💧 Goal hit! +10 Phoenix Tokens — streak ${data.water_streak.current}🔥`, { duration: 4500 });
      }
      if ((data.new_badges || []).length) {
        toast.success(`🏆 New badge unlocked!`);
      }
      if (data.vitals_mega?.awarded) {
        toast.success(`🐦‍🔥 TOTAL PHOENIX! +${data.vitals_mega.bonus_pt} Phoenix Tokens — all 6 vitals tiles cleared this week!`, { duration: 8000 });
      }
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed.");
    } finally {
      setBusy(false);
    }
  };

  const undo = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/vitals/water/last`);
      toast.success(`↶ Removed ${data.removed_oz}oz`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Undo failed.");
    } finally {
      setBusy(false);
    }
  };

  const saveCustom = async () => {
    const oz = parseInt(customOz, 10);
    if (!oz || oz <= 0) {
      toast.error("Enter a positive number.");
      return;
    }
    await addOz(oz, "custom");
    setCustomOz("");
    setShowCustom(false);
  };

  const saveGoal = async () => {
    const g = parseInt(newGoal, 10);
    if (!g || g < 16 || g > 400) {
      toast.error("Goal must be 16-400 oz.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/vitals/water/goal`, { goal_oz: g });
      toast.success(`Goal set to ${g} oz`);
      setShowGoal(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const pct = Math.min(100, Math.round(((w.total_oz || 0) / Math.max(1, w.goal_oz)) * 100));

  return (
    <div data-testid="vitals-water-card" className="bg-[#0F0F0F] border border-zinc-800 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-sky-300 font-bold flex items-center gap-1.5">
            <Droplet size={11} /> Water
          </div>
          <h3 className="font-display text-2xl text-white">Hydration</h3>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">Streak</div>
          <div className="font-display text-2xl text-[#D4AF37] tabular-nums">{streak.current}d</div>
        </div>
      </div>

      {/* Progress */}
      <div className="mb-2 flex items-baseline justify-between">
        <span>
          <span data-testid="vitals-water-total" className="font-display text-5xl text-white tabular-nums">
            {w.total_oz}
          </span>
          <span className="text-sm text-neutral-500"> / {w.goal_oz} oz</span>
        </span>
        <button
          data-testid="vitals-water-goal-btn"
          onClick={() => setShowGoal((v) => !v)}
          className="text-xs text-neutral-500 hover:text-[#D4AF37] inline-flex items-center gap-1"
        >
          <Settings size={11} />
        </button>
      </div>
      <div className="h-2 bg-zinc-900 mb-4 overflow-hidden">
        <div
          data-testid="vitals-water-bar"
          className="h-full bg-gradient-to-r from-sky-400 to-cyan-300 transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      {showGoal && (
        <div className="border border-zinc-800 p-3 mb-3">
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">
            Daily Goal (16-400 oz)
          </label>
          <div className="flex gap-2">
            <input
              data-testid="vitals-water-goal-input"
              type="number"
              value={newGoal}
              onChange={(e) => setNewGoal(e.target.value)}
              className="input-omni flex-1"
            />
            <button
              data-testid="vitals-water-goal-save"
              onClick={saveGoal}
              disabled={busy}
              className="btn-omni btn-red text-xs disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Add buttons */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {[8, 16, 24].map((oz) => (
          <button
            key={oz}
            data-testid={`vitals-water-add-${oz}`}
            onClick={() => addOz(oz)}
            disabled={busy}
            className="py-3 bg-[#0A0A0A] border-2 border-zinc-800 hover:border-sky-400 hover:bg-sky-400/5 transition-colors disabled:opacity-50"
          >
            <Plus size={14} className="text-sky-300 mx-auto mb-0.5" />
            <span className="block text-sm font-display text-white">+{oz} oz</span>
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          data-testid="vitals-water-custom-btn"
          onClick={() => setShowCustom((v) => !v)}
          className="flex-1 text-xs uppercase tracking-widest text-neutral-400 hover:text-white border border-zinc-800 hover:border-zinc-600 py-2"
        >
          Custom oz
        </button>
        {w.entries?.length > 0 && (
          <button
            data-testid="vitals-water-undo"
            onClick={undo}
            disabled={busy}
            className="text-xs uppercase tracking-widest text-neutral-500 hover:text-[#DC2626] border border-zinc-800 hover:border-[#DC2626] py-2 px-3 inline-flex items-center gap-1 disabled:opacity-50"
            title="Undo last add"
          >
            <RotateCcw size={11} /> Undo
          </button>
        )}
      </div>

      {showCustom && (
        <div className="flex gap-2 mt-2">
          <input
            data-testid="vitals-water-custom-input"
            type="number"
            value={customOz}
            onChange={(e) => setCustomOz(e.target.value)}
            placeholder="oz (max 64)"
            min="1"
            max="64"
            className="input-omni flex-1 text-sm"
          />
          <button
            data-testid="vitals-water-custom-save"
            onClick={saveCustom}
            disabled={busy}
            className="btn-omni btn-red text-xs disabled:opacity-50"
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}

/* ============ Quick Log Card ============ */
function QuickLogCard({ summary, onChange }) {
  const entries = summary?.quick || [];
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("shake");
  const [name, setName] = useState("");
  const [calories, setCalories] = useState("");
  const [time, setTime] = useState("");
  const [guiltFree, setGuiltFree] = useState(false);
  const [busy, setBusy] = useState(false);
  // 🍳 Optional macros — a collapsed panel so calorie-only logging stays
  // one-tap while power users can drop in full P/C/F/fiber/sugar/sodium.
  // Any left blank → server stores null and the nutrition tracker treats
  // as 0 (no phantom macros invented). Feb 2026: Snap-a-Meal already
  // fills these automatically; this panel is the manual fallback.
  const [showMacros, setShowMacros] = useState(false);
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fat, setFat] = useState("");
  const [fiber, setFiber] = useState("");
  const [sugar, setSugar] = useState("");
  const [sodium, setSodium] = useState("");

  const reset = () => {
    setName("");
    setCalories("");
    setTime("");
    setGuiltFree(false);
    setShowMacros(false);
    setProtein(""); setCarbs(""); setFat("");
    setFiber("");   setSugar(""); setSodium("");
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Add a name.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        type,
        name: name.trim(),
        time: time || "",
        guilt_free: type === "cheat" ? guiltFree : false,
      };
      if (calories) payload.calories = parseInt(calories, 10);
      // Only send macros the user actually filled in — leave null for the rest.
      if (protein) payload.protein_g = parseInt(protein, 10);
      if (carbs)   payload.carbs_g   = parseInt(carbs,   10);
      if (fat)     payload.fat_g     = parseInt(fat,     10);
      if (fiber)   payload.fiber_g   = parseInt(fiber,   10);
      if (sugar)   payload.sugar_g   = parseInt(sugar,   10);
      if (sodium)  payload.sodium_mg = parseInt(sodium,  10);
      const { data } = await axios.post(`${API}/vitals/quick`, payload);
      toast.success(`✅ Logged ${data.entry.name} (+2 PT)`, { duration: 3500 });
      if ((data.new_badges || []).length) {
        toast.success(`🏆 New badge unlocked!`);
      }
      if (data.vitals_mega?.awarded) {
        toast.success(`🐦‍🔥 TOTAL PHOENIX! +${data.vitals_mega.bonus_pt} Phoenix Tokens — all 6 vitals tiles cleared this week!`, { duration: 8000 });
      }
      reset();
      setOpen(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/vitals/quick/${id}`);
      onChange?.();
    } catch (e) {
      toast.error("Delete failed.");
    }
  };

  return (
    <div data-testid="vitals-quick-card" className="bg-[#0F0F0F] border border-zinc-800 p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-bold flex items-center gap-1.5">
            <ListPlus size={11} /> Quick Log
          </div>
          <h3 className="font-display text-2xl text-white">Today's Extras</h3>
        </div>
        <button
          data-testid="vitals-quick-new"
          onClick={() => setOpen(true)}
          className="w-9 h-9 bg-[#D4AF37] hover:bg-[#f5d96b] text-black flex items-center justify-center"
          title="Quick add"
        >
          <Plus size={16} strokeWidth={2.5} />
        </button>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
        Shakes · Pre-workout · Supplements · Restaurants · Cheat meals
      </div>

      <div className="mb-3">
        <FoodPhotoButton
          variant="ghost"
          label="📷 Snap a Meal (AI)"
          quickLogType="other"
          onSaved={() => onChange?.()}
        />
      </div>

      {entries.length === 0 ? (
        <p className="text-xs text-neutral-500 py-6 text-center italic">
          Nothing logged yet. Tap + to add a shake, supplement, or anything off-plan.
        </p>
      ) : (
        <ul data-testid="vitals-quick-list" className="space-y-2 max-h-[340px] overflow-y-auto">
          {entries
            .slice()
            .reverse()
            .map((e) => {
              const meta = QUICK_TYPES.find((t) => t.key === e.type) || QUICK_TYPES[5];
              return (
                <li
                  key={e.id}
                  data-testid={`vitals-quick-item-${e.id}`}
                  className="flex items-center gap-2 p-2 bg-[#0A0A0A] border border-zinc-900 group"
                >
                  <span className="text-lg w-6 text-center">{meta.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate font-display">
                      {e.name}
                      {e.guilt_free && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-widest text-emerald-300 border border-emerald-700/50 px-1">
                          Guilt-free
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                      {meta.label}
                      {e.calories ? <> · {e.calories} cal</> : null}
                      {e.time ? <> · {e.time}</> : null}
                      {/* 🍳 Show P/C/F inline whenever they were captured so the
                          user sees at-a-glance which entries have full macros.
                          For calorie-only entries, offer a one-tap AI Estimate. */}
                      {(e.protein_g || e.carbs_g || e.fat_g) ? (
                        <span className="text-emerald-300/80 ml-1" data-testid={`vitals-quick-macros-${e.id}`}>
                          · P{e.protein_g ?? 0}g · C{e.carbs_g ?? 0}g · F{e.fat_g ?? 0}g
                        </span>
                      ) : e.calories ? (
                        <span className="ml-1 inline-flex items-center gap-1">
                          <span className="text-neutral-600 italic">· macros not tracked</span>
                          <RetroMacroBackfill entry={e} onSaved={() => onChange?.()} />
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <button
                    data-testid={`vitals-quick-del-${e.id}`}
                    onClick={() => remove(e.id)}
                    className="text-neutral-700 hover:text-[#DC2626] opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Remove"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              );
            })}
        </ul>
      )}

      {open && (
        <div
          data-testid="vitals-quick-modal"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-md w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(ev) => ev.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold">
                  Quick Log
                </div>
                <h3 className="font-display text-2xl text-white">Add Entry</h3>
              </div>
              <button
                data-testid="vitals-quick-close"
                onClick={() => setOpen(false)}
                className="text-neutral-500 hover:text-white"
              >
                <XIcon size={22} />
              </button>
            </div>
            {/* Type selector */}
            <div className="grid grid-cols-3 gap-1.5 mb-3">
              {QUICK_TYPES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`vitals-quick-type-${t.key}`}
                  onClick={() => setType(t.key)}
                  className={`py-2 border-2 text-[10px] uppercase tracking-widest transition-colors ${
                    type === t.key
                      ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white"
                      : "border-zinc-800 text-neutral-400 hover:border-zinc-600"
                  }`}
                >
                  <span className="block text-lg leading-none mb-0.5">{t.icon}</span>
                  {t.label}
                </button>
              ))}
            </div>
            <input
              data-testid="vitals-quick-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                type === "shake"
                  ? "e.g. Whey + banana"
                  : type === "supplement"
                  ? "e.g. Creatine 5g"
                  : type === "restaurant"
                  ? "e.g. Chipotle bowl"
                  : type === "cheat"
                  ? "e.g. Slice of pizza"
                  : "Name"
              }
              maxLength={80}
              className="input-omni w-full mb-2"
            />
            <div className="grid grid-cols-2 gap-2 mb-2">
              <input
                data-testid="vitals-quick-calories"
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
                placeholder="Calories (opt)"
                min="0"
                max="5000"
                className="input-omni"
              />
              <input
                data-testid="vitals-quick-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="input-omni"
              />
            </div>
            {type === "cheat" && (
              <label className="flex items-center gap-2 text-xs text-neutral-300 mb-3 cursor-pointer">
                <input
                  data-testid="vitals-quick-guilt"
                  type="checkbox"
                  checked={guiltFree}
                  onChange={(e) => setGuiltFree(e.target.checked)}
                  className="accent-[#D4AF37]"
                />
                Mark as guilt-free — you earned it 💪
              </label>
            )}
            {/* 🍳 Optional macros — Feb 2026: keeps calorie-only path one-tap
                while letting power users capture full P/C/F/fiber/sugar/sodium
                so the Nutrition donut + macro tracker reflect the entry. */}
            <button
              type="button"
              onClick={() => setShowMacros((v) => !v)}
              className="w-full text-left text-[10px] uppercase tracking-widest text-emerald-300/80 hover:text-emerald-200 mb-2 flex items-center gap-1"
              data-testid="vitals-quick-macros-toggle"
            >
              <span>{showMacros ? "▾" : "▸"} Optional macros {showMacros ? "" : "(tap to expand)"}</span>
            </button>
            {showMacros && (
              <div className="grid grid-cols-3 gap-1.5 mb-3" data-testid="vitals-quick-macros-fields">
                <input
                  data-testid="vitals-quick-protein"
                  type="number" min="0" max="500"
                  value={protein}
                  onChange={(e) => setProtein(e.target.value)}
                  placeholder="Protein g"
                  className="input-omni text-sm"
                />
                <input
                  data-testid="vitals-quick-carbs"
                  type="number" min="0" max="800"
                  value={carbs}
                  onChange={(e) => setCarbs(e.target.value)}
                  placeholder="Carbs g"
                  className="input-omni text-sm"
                />
                <input
                  data-testid="vitals-quick-fat"
                  type="number" min="0" max="400"
                  value={fat}
                  onChange={(e) => setFat(e.target.value)}
                  placeholder="Fat g"
                  className="input-omni text-sm"
                />
                <input
                  data-testid="vitals-quick-fiber"
                  type="number" min="0" max="200"
                  value={fiber}
                  onChange={(e) => setFiber(e.target.value)}
                  placeholder="Fiber g"
                  className="input-omni text-sm"
                />
                <input
                  data-testid="vitals-quick-sugar"
                  type="number" min="0" max="500"
                  value={sugar}
                  onChange={(e) => setSugar(e.target.value)}
                  placeholder="Sugar g"
                  className="input-omni text-sm"
                />
                <input
                  data-testid="vitals-quick-sodium"
                  type="number" min="0" max="8000"
                  value={sodium}
                  onChange={(e) => setSodium(e.target.value)}
                  placeholder="Sodium mg"
                  className="input-omni text-sm"
                />
              </div>
            )}
            <button
              data-testid="vitals-quick-save"
              onClick={submit}
              disabled={busy}
              className="btn-omni btn-red w-full disabled:opacity-50"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              Log Entry (+2 PT)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
